import {
  Box,
  Heading,
  Text,
  List,
  ListItem,
  Link,
  useStyleConfig,
  Button,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";

const TermsOfService = () => {
  const sectionStyles = useStyleConfig("Section");
  const highlightBoxStyles = useStyleConfig("HighlightBox");
  const navigate = useNavigate();

  return (
    <Box height="100vh" overflowY="auto" bg="gray.50" p={{ base: 4, md: 8 }}>
      <Box
        maxW="4xl"
        mx="auto"
        bg="white"
        borderRadius="lg"
        boxShadow="sm"
        p={{ base: 6, md: 8 }}
      >
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="link"
          color="blue.600"
          mb={4}
          onClick={() => navigate("/")} // or '/login' based on your route
        >
          Back to Login
        </Button>
        <Box borderBottom="1px" borderColor="gray.200" pb={6} mb={8}>
          <Heading as="h1" size="xl" mb={4} color="gray.800">
            Terms of Service
          </Heading>
          <Box color="gray.600" spacing={2}>
            <Text>Effective Date: May 20, 2025</Text>
            <Text>Last Updated: May 20, 2025</Text>
          </Box>
        </Box>

        <Box sx={{ p: { mb: 6 }, ul: { mb: 4 } }}>
          <Text mb={6}>
            Welcome to the CM Hub (the "Service"), operated by Creatives Matter
            LLC ("we", "us", or "our"). These Terms of Service ("Terms") govern
            your access to and use of the Service.
          </Text>

          <Text fontWeight="semibold" mb={8}>
            By using the Service, you agree to be bound by these Terms.
          </Text>

          <Section title="1. Use of the Service">
            <Text>
              You may use the Service only if you are at least 18 years old and
              have the legal capacity to enter into a binding contract.
            </Text>
          </Section>

          <Section title="2. Your Account">
            <Text>
              To access certain features, you must create an account. You are
              responsible for:
            </Text>
            <List styleType="disc" pl={6} spacing={2} mt={2}>
              <ListItem>Providing accurate information</ListItem>
              <ListItem>Keeping login credentials secure</ListItem>
              <ListItem>All activities under your account</ListItem>
            </List>
            <Text mt={4}>
              We may suspend or terminate your account for unauthorized use or
              violations.
            </Text>
          </Section>

          <Section title="3. Ownership and Use of Data">
            <Text>
              You retain ownership of the data you upload to the Service, including sales data, customer information, and email content. You are solely responsible for ensuring that such data is accurate, lawful, and submitted with the appropriate consents.
            </Text>
            <Text>
             By using the Service, you grant us a perpetual, irrevocable, worldwide, non-exclusive, royalty-free license to use, process, analyze, copy, distribute, display, publish, and otherwise exploit your customer information for any purpose, including but not limited to commercial, analytical, product development, and marketing purposes.
            </Text>
            <Text>
             You confirm that you have obtained all necessary rights and permissions to provide customer information to the Service and that doing so does not violate any applicable laws or third-party rights.
            </Text>
            <Text>
             We are not obligated to delete, anonymize, or return customer information once it has been added to the system, including in the event of account closure or data deletion requests, unless required by applicable law.
            </Text>
            <Text>
            Please note that outside of your customer information, we use your personal data (such as your name and email address) solely to provide, support, and improve the Service in accordance with our Privacy Policy.
            </Text>
          </Section>

          <Section title="4. Email Integration">
            <Box {...highlightBoxStyles}>
              <Text>
                You authorize us to send emails via your connected account 
                (e.g., invoices, sales emails). We do not access or store inbox
                content.
              </Text>
              <Text mt={4}>
                You must comply with applicable laws like CAN-SPAM and GDPR.
              </Text>
            </Box>
          </Section>

          <Section title="5. Acceptable Use">
            <Text>You agree not to:</Text>
            <List styleType="disc" pl={6} spacing={2} mt={2}>
              <ListItem>Use the Service unlawfully or fraudulently</ListItem>
              <ListItem>Send spam</ListItem>
              <ListItem>Upload viruses or malicious code</ListItem>
              <ListItem>Interfere with security or performance</ListItem>
            </List>
            <Text mt={4}>
              Violations may result in suspension or termination.
            </Text>
          </Section>
          <Section title="6. Promotional Representation and Campaign Management">
            <Text>
              By using our services, you authorize us to include you in
              promotional materials distributed to brands and agencies. If a
              brand or agency expresses interest in a potential collaboration
              with you through our platform, we will act as your representative
              in initial negotiations. You retain the right to approve or
              decline any opportunity presented to you.
            </Text>
            <Text>
              Upon your approval of an opportunity, we will manage the campaign
              and handle all communications and payment processes with the brand
              or agency. You will be compensated based on the agreed-upon fee
              for each approved opportunity, minus any applicable processing
              fees. Payment will be issued to you within five (5) business days
              of our receipt of funds from the brand or agency.
            </Text>
            <Text>
              We do not withhold taxes from your payments. You are solely
              responsible for reporting and paying any taxes owed in connection
              with payments received.
            </Text>
          </Section>
          <Section title="7. Fees and Payment">
            <Text>
              If your use of the Service requires payment, you agree to pay all
              applicable fees as described at the time of purchase. All payments
              are non-refundable unless stated otherwise.
            </Text>
          </Section>

          <Section title="8. Termination">
            <Text>
              You may stop using the Service at any time. We may suspend or
              terminate access for violations or other reasons with notice.
            </Text>
          </Section>

          <Section title="9. Intellectual Property">
            <Text>
              All content and functionality (except your data) belong to
              Creatives Matter LLC. Do not copy or distribute without
              permission.
            </Text>
          </Section>

          <Section title="10. Disclaimer of Warranties">
            <Text>
              The Service is provided "as is" without warranties. We do not
              guarantee error-free or uninterrupted service.
            </Text>
          </Section>

          <Section title="11. Limitation of Liability">
            <Text>
              We are not liable for indirect or consequential damages. Our total
              liability is limited to fees paid in the past 12 months, if any.
            </Text>
          </Section>

          <Section title="12. Changes to These Terms">
            <Text>
              We may update these Terms. Continued use means you accept the new
              Terms.
            </Text>
          </Section>

          <Section title="13. Governing Law">
            <Text>
              These Terms are governed by the laws of the state of Texas. Legal
              actions must be brought in the courts of the state of Texas.
            </Text>
          </Section>

          <Section title="14. Contact Us">
            <Box bg="gray.50" p={6} borderRadius="lg">
              <Text fontWeight="semibold" mb={2}>
                For questions, contact us at:
              </Text>
              <Text mb={1}>Creatives Matter LLC</Text>
              <Link
                href="mailto:info@creativesmatter.com"
                color="blue.600"
                _hover={{ textDecoration: "underline" }}
              >
                info@creativesmatter.com
              </Link>
            </Box>
          </Section>
        </Box>
      </Box>
    </Box>
  );
};

const Section = ({ title, children }) => (
  <Box mb={8}>
    <Heading as="h2" size="md" mb={4} color="gray.800">
      {title}
    </Heading>
    {children}
  </Box>
);

// Reuse the same theme configurations from Privacy Policy
const theme = {
  components: {
    Section: {
      baseStyle: {
        mb: 8,
      },
    },
    HighlightBox: {
      baseStyle: {
        bg: "blue.50",
        p: 4,
        borderRadius: "md",
      },
    },
  },
};

export default TermsOfService;
